import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import useU2000AxiosAuthenticated from '../../../u2000Core/axios/U2000UseAxiosAuthenticated';
import { U2000ExtractFirstApiError } from '../../../u2000Core/axios/U2000Axios';
import { U2000HandleApiError, type U2000ApiErrorResult } from '../../../u2000Core/axios/U2000HandleApiError';
import { U3121_ERROR_PROJECT_NUMBER_DUPLICATE } from '../../U3121Service';
import { useToast } from '../../../u2000Core/context/toast/U2000ToastContext';
import { doUploadProjectFromFile, luHandleAppiError, type U3121Project } from '../../U3121Service';
import {
  type U3121UploadFileLineResult,
  u3121ValidateUploadFile,
} from '../../tools/U3121UploadProjectFromFileBatchTools';
import { NS } from '../../U3121';

export interface UseU3121UploadProjectFromFileHandlersParams {
  onSuccess?: (project: U3121Project) => void;
  /** Message utilisateur résolu (i18n / API) — ex. affichage dans le dialogue d'upload. */
  onError?: (message: string) => void;
  /** false : pas de toast d'erreur (le dialogue affiche le message). Défaut true. */
  showErrorToast?: boolean;
}

/** Descriptions API génériques (souvent EN) — ne pas les afficher telles quelles à l'usager. */
function u3121IsOpaqueApiDescription(text: string): boolean {
  const normalized = text.trim().toLowerCase();
  return (
    normalized === 'error! see details' ||
    normalized === 'see details' ||
    normalized === 'error!' ||
    normalized.startsWith('error! see details')
  );
}

/** Clé i18n non résolue (souvent `errorTRM-B9999` absent des locales). */
function u3121LooksLikeMissingTranslationKey(text: string): boolean {
  const trimmed = text.trim();
  return /^error[A-Z0-9-]+$/i.test(trimmed) || trimmed === 'handleApiError: Invalid error object';
}

function u3121GetHttpStatus(error: unknown): number | undefined {
  const err = error as { status?: number; response?: { status?: number } };
  const status = err?.status ?? err?.response?.status;
  return typeof status === 'number' && status > 0 ? status : undefined;
}

function u3121IsDisplayableErrorText(text: string | undefined): text is string {
  if (!text?.trim()) return false;
  if (u3121IsOpaqueApiDescription(text)) return false;
  if (u3121LooksLikeMissingTranslationKey(text)) return false;
  return true;
}

function u3121ExtractApiErrorEntries(error: unknown): Array<{ errorCode?: string; description?: string }> {
  const err = error as {
    data?: { errors?: Array<{ errorCode?: string; description?: string }> };
    response?: { data?: { errors?: Array<{ errorCode?: string; description?: string }> } };
  };
  return err?.data?.errors ?? err?.response?.data?.errors ?? [];
}

function u3121TranslateKnownUploadErrorCode(
  errorCode: string | undefined,
  t: ReturnType<typeof useTranslation>['t']
): string | null {
  if (!errorCode) return null;
  if (errorCode === U3121_ERROR_PROJECT_NUMBER_DUPLICATE) {
    return t('errProjectNumberDuplicate', { ns: NS });
  }
  for (const ns of [NS, 'u3120', 'common'] as const) {
    const keyed = t(`error${errorCode}`, { ns, defaultValue: '' });
    if (keyed && keyed !== `error${errorCode}`) return keyed;
  }
  return null;
}

function u3121CollectApiErrorTexts(errorResult: U2000ApiErrorResult): string[] {
  const texts: string[] = [];
  errorResult.genericErrors?.forEach((e) => {
    const text = e.text?.trim();
    if (u3121IsDisplayableErrorText(text)) texts.push(text);
  });
  Object.values(errorResult.fieldErrors || {}).forEach((text) => {
    if (u3121IsDisplayableErrorText(text)) texts.push(text.trim());
  });
  return texts;
}

function u3121UploadFallbackErrorMessage(error: unknown, t: ReturnType<typeof useTranslation>['t']): string {
  const entries = u3121ExtractApiErrorEntries(error);
  for (const entry of entries) {
    const mapped = u3121TranslateKnownUploadErrorCode(entry.errorCode, t);
    if (mapped) return mapped;
  }

  const first = U2000ExtractFirstApiError(error);
  const mappedFirst = u3121TranslateKnownUploadErrorCode(first?.errorCode, t);
  if (mappedFirst) return mappedFirst;

  for (const entry of entries) {
    const description = entry.description?.trim();
    if (description && !u3121IsOpaqueApiDescription(description)) {
      return description;
    }
  }

  const firstCode = entries[0]?.errorCode ?? first?.errorCode;
  const httpStatus = u3121GetHttpStatus(error);

  if (firstCode === 'B0007' || (httpStatus === 400 && !firstCode)) {
    return t('msgUploadSidecarValidationFailed', { ns: NS });
  }
  if (httpStatus === 413) {
    return t('errorBU312102FileSize', { ns: NS });
  }
  if (firstCode) {
    return t('errorTRM-B997', {
      ns: NS,
      errorCode: firstCode,
      errorMessage: t('msgUploadProjectFromFileFailed', { ns: NS }),
    });
  }
  if (httpStatus != null && httpStatus >= 500) {
    return t('errorB9999', { ns: 'common' });
  }

  return t('msgUploadProjectFromFileFailed', { ns: NS });
}

/** Résout un message d'erreur upload affichable (dialogue, récap, toast). */
export function u3121ResolveUploadProjectFromFileErrorMessage(
  error: unknown,
  t: ReturnType<typeof useTranslation>['t']
): string {
  const errorResult = U2000HandleApiError(error as Parameters<typeof U2000HandleApiError>[0], t, NS, luHandleAppiError);
  const collected = u3121CollectApiErrorTexts(errorResult);
  if (collected.length > 0) {
    return collected.join(' ');
  }
  return u3121UploadFallbackErrorMessage(error, t);
}

/** Handlers pour créer un projet à partir d'un fichier texte (POST multipart uploadProjectFromFile). */
export function useU3121UploadProjectFromFileHandlers(params: UseU3121UploadProjectFromFileHandlersParams) {
  const { onSuccess, onError, showErrorToast = true } = params;
  const axios = useU2000AxiosAuthenticated();
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();

  const uploadProjectFromFileOnce = useCallback(
    async (formData: FormData, fileName: string): Promise<U3121UploadFileLineResult> => {
      try {
        const response = await doUploadProjectFromFile(axios, formData);
        const project = (response.data?.result ?? response.data) as U3121Project;
        if (project?.id != null) {
          return {
            fileName,
            status: 'success',
            projectId: project.id,
            projetNumber: project.projetNumber ?? null,
          };
        }
        return {
          fileName,
          status: 'error',
          errorMessage: t('msgUploadInvalidResponse', { ns: NS }),
        };
      } catch (error: unknown) {
        return {
          fileName,
          status: 'error',
          errorMessage: u3121ResolveUploadProjectFromFileErrorMessage(error, t),
        };
      }
    },
    [axios, t]
  );

  const uploadProjectFromFilesSequential = useCallback(
    async (
      files: File[],
      onProgress?: (current: number, total: number, fileName: string) => void
    ): Promise<U3121UploadFileLineResult[]> => {
      const results: U3121UploadFileLineResult[] = [];
      const total = files.length;

      for (let i = 0; i < files.length; i += 1) {
        const file = files[i];
        onProgress?.(i + 1, total, file.name);

        const validationKey = u3121ValidateUploadFile(file);
        if (validationKey) {
          results.push({
            fileName: file.name,
            status: 'error',
            errorMessage: t(validationKey, { ns: NS }),
          });
          continue;
        }

        const formData = new FormData();
        formData.append('file', file);
        const line = await uploadProjectFromFileOnce(formData, file.name);
        results.push(line);
      }

      return results;
    },
    [t, uploadProjectFromFileOnce]
  );

  const uploadProjectFromFile = useCallback(
    async (formData: FormData) => {
      try {
        const response = await doUploadProjectFromFile(axios, formData);
        const project = (response.data?.result ?? response.data) as U3121Project;
        if (project?.id != null) {
          toast.success(t('msgProjectCreatedFromFile', { ns: NS }));
          onSuccess?.(project);
          navigate(`/U3121/${project.id}`, { replace: true });
        } else {
          const message = t('msgUploadInvalidResponse', { ns: NS });
          if (showErrorToast) {
            toast.error(message, t('errorTitle', { ns: NS }));
          }
          onError?.(message);
        }
      } catch (error: unknown) {
        const message = u3121ResolveUploadProjectFromFileErrorMessage(error, t);
        if (showErrorToast) {
          toast.error(message, t('errorTitle', { ns: NS }));
        }
        onError?.(message);
        throw error;
      }
    },
    [axios, navigate, onError, onSuccess, showErrorToast, t, toast]
  );

  return { uploadProjectFromFile, uploadProjectFromFilesSequential };
}
